/**
 * Очередь подтверждения оборудования: вход-плашка и экран разбора.
 *
 * Зачем шаг подтверждения вообще существует: три человека заведут одну печь как
 * «печь», «Печь 2» и «печка большая», и история обслуживания размажется по трём
 * карточкам. Здесь дубли склеиваются, пока их трое, а не тридцать.
 *
 * ⚠️ Отдельный экран, а не фильтр в карточке Объекта. После обхода прилетает
 * сорок единиц с разных Объектов сразу; разбирать их, заходя в каждый Объект по
 * очереди, — это ровно та офисная работа, от которой модуль уходил.
 *
 * Компонент сам решает, показываться ли: нет права подтверждать или очередь
 * пуста — не рендерится вовсе. Поэтому его можно безусловно вставлять на любой
 * дашборд.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { api } from '../api/client';
import Icon from './Icon';
import { usePerm } from '../hooks/usePerm';
import { track } from '../analytics';
import { backdropClose } from '../utils/modalBackdrop';
import EquipmentModal from './EquipmentModal';

/**
 * Одна строка очереди. На уровне модуля: компонент, объявленный внутри
 * другого, пересоздаётся на каждом рендере и теряет состояние.
 */
function QueueRow({ item, busy, onOpen, onConfirm, onMerge, onReject, t }) {
  return (
    <div className="card" style={{ padding: 12, marginBottom: 8 }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
        {item.photo_thumb_url && (
          <img src={item.photo_thumb_url} alt="" loading="lazy"
               onClick={onOpen}
               style={{ width: 52, height: 52, borderRadius: 8, objectFit: 'cover',
                        flexShrink: 0, background: 'var(--bg3)', cursor: 'pointer' }} />
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 14, cursor: 'pointer' }} onClick={onOpen}>
            {item.name}
          </div>
          <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 2 }}>
            {[item.manufacturer, item.model].filter(Boolean).join(' ')}
            {item.serial_no ? ` · ${item.serial_no}` : ''}
          </div>
          <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>
            {item.property_name}{item.created_by_name ? ` · ${item.created_by_name}` : ''}
          </div>
        </div>
      </div>

      {/* ⚠️ Похожие — главное на этой строке, ради них экран и нужен. Показываем
          их РЯДОМ с записью, а не прячем за кликом: решение «дубль или нет»
          принимается сравнением двух названий, и лишний переход его срывает. */}
      {item.duplicates.length > 0 && (
        <div style={{
          marginTop: 10, padding: '8px 10px', borderRadius: 8,
          background: 'rgba(217,119,6,0.10)', border: '1px solid var(--orange)',
        }}>
          <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 6 }}>
            {t('equipmentQueue.looksLike')}
          </div>
          {item.duplicates.map((d) => (
            <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
              <span style={{ fontSize: 13, fontWeight: 600, flex: 1, minWidth: 0 }}>
                {d.name}
                {d.serial_no === item.serial_no && item.serial_no && (
                  <span style={{ color: 'var(--text3)', fontWeight: 400 }}> · {t('equipmentQueue.sameSerial')}</span>
                )}
              </span>
              <button className="btn btn-sm btn-primary" data-ph-capture="equipment_merge"
                      disabled={busy} onClick={() => onMerge(d)}>
                {t('equipmentQueue.merge')}
              </button>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
        <button className="btn btn-sm btn-primary" data-ph-capture="equipment_queue_confirm"
                disabled={busy} onClick={onConfirm}>
          {t('equipmentQueue.confirm')}
        </button>
        <button className="btn btn-sm" data-ph-capture="equipment_queue_reject"
                disabled={busy} onClick={onReject}>
          {t('equipmentQueue.reject')}
        </button>
      </div>
    </div>
  );
}

export default function EquipmentQueue({ onOpenTask }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // Дашборды рендерят очередь без onOpenTask — и клик по Работе в карточке
  // ничего не делал. Открываем Работу на странице её Объекта, тем же
  // приёмом, что FAB-сканер и «Парк» (state.openTaskId).
  const openTask = onOpenTask || ((taskId, propId) => propId && navigate(`/properties/${propId}`, {
    state: { openTaskId: taskId },
  }));
  const { can } = usePerm();

  const [count, setCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [cardId, setCardId] = useState(null);
  const [selected, setSelected] = useState(() => new Set());

  const canConfirm = can('equipment.confirm');

  const loadCount = useCallback(() => {
    if (!canConfirm) return;
    api.equipmentUnconfirmedCount()
      .then((d) => setCount(d.count || 0))
      .catch(() => setCount(0));
  }, [canConfirm]);

  useEffect(() => { loadCount(); }, [loadCount]);

  const loadQueue = useCallback(() => {
    setItems(null);
    api.equipmentUnconfirmed()
      .then((d) => setItems(d.items || []))
      .catch(() => setItems([]));
  }, []);

  useEffect(() => { if (open) loadQueue(); }, [open, loadQueue]);

  const after = () => { loadQueue(); loadCount(); setSelected(new Set()); };

  const confirmOne = async (item) => {
    setBusyId(item.id);
    try {
      await api.equipmentConfirm(item.id);
      track('equipment_confirm', { bulk: false, count: 1, source: 'queue' });
      after();
    } catch (e) {
      toast.error(e?.message || t('equipmentQueue.failed'));
    } finally { setBusyId(null); }
  };

  const confirmSelected = async () => {
    const ids = [...selected];
    if (!ids.length) return;
    try {
      await api.equipmentConfirmBulk(ids);
      track('equipment_confirm', { bulk: true, count: ids.length, source: 'queue' });
      after();
    } catch (e) {
      toast.error(e?.message || t('equipmentQueue.failed'));
    }
  };

  const mergeInto = async (item, target) => {
    // ⚠️ Спрашиваем: объединение необратимо и переносит историю. Называем обе
    // единицы — в очереди из сорока строк промахнуться легко.
    if (!window.confirm(t('equipmentQueue.mergeConfirm', { from: item.name, to: target.name }))) return;
    setBusyId(item.id);
    try {
      await api.equipmentMerge(item.id, target.id);
      track('equipment_merge', { same_serial: !!item.serial_no && item.serial_no === target.serial_no });
      after();
    } catch (e) {
      toast.error(e?.message || t('equipmentQueue.failed'));
    } finally { setBusyId(null); }
  };

  const reject = async (item) => {
    if (!window.confirm(t('equipmentQueue.rejectConfirm', { name: item.name }))) return;
    setBusyId(item.id);
    try {
      const r = await api.equipmentReject(item.id);
      track('equipment_reject', { tags_freed: r.tags_freed || 0 });
      // Наклейка вернулась в оборот — про это надо сказать, иначе прораб не
      // поймёт, что стикер на железе снова можно использовать.
      toast.success(r.tags_freed
        ? t('equipmentQueue.rejectedWithTag', { count: r.tags_freed })
        : t('equipmentQueue.rejected'));
      after();
    } catch (e) {
      toast.error(e?.message || t('equipmentQueue.failed'));
    } finally { setBusyId(null); }
  };

  // Нет права или пустая очередь — блока нет вовсе. Не пустое состояние, а
  // отсутствие: тревожная цифра 0 на дашборде никому не нужна.
  if (!canConfirm || (!count && !open)) return null;

  const toggle = (id) => setSelected((prev) => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  return (
    <>
      <div className="card clickable"
           data-ph-capture="equipment_queue_open"
           onClick={() => { setOpen(true); track('equipment_queue_opened', { count }); }}
           style={{ padding: '14px 16px', marginBottom: 12, cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: 10,
                    borderLeft: '3px solid var(--orange)' }}>
        <Icon name="tool" size={18} style={{ color: 'var(--text2)' }} />
        <span style={{ flex: 1, fontSize: 14, fontWeight: 600 }}>
          {t('equipmentQueue.tile', { count })}
        </span>
        <Icon name="chevronRight" size={16} style={{ color: 'var(--text3)' }} />
      </div>

      {open && (
        <div className="modal-overlay" {...backdropClose(() => setOpen(false))}>
          <div className="modal" style={{ maxWidth: 620 }}>
            <div className="modal-header">
              <h3 style={{ margin: 0 }}>{t('equipmentQueue.title')}</h3>
              <button className="modal-close" onClick={() => setOpen(false)}
                      aria-label={t('common.close')}>×</button>
            </div>

            <div className="modal-body">
              {items === null ? (
                <div style={{ padding: '28px 0', textAlign: 'center', fontSize: 13, color: 'var(--text3)' }}>
                  {t('common.loading')}
                </div>
              ) : items.length === 0 ? (
                <div style={{ padding: '28px 0', textAlign: 'center', fontSize: 13, color: 'var(--text3)' }}>
                  {t('equipmentQueue.empty')}
                </div>
              ) : (
                <>
                  {/* Массовое подтверждение — обязательная часть: после обхода
                      прилетает сорок единиц, и подтверждать их по одной никто
                      не станет. */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10,
                                flexWrap: 'wrap' }}>
                    <button className="btn btn-sm" onClick={() =>
                      setSelected(selected.size === items.length ? new Set() : new Set(items.map((i) => i.id)))}>
                      {selected.size === items.length ? t('equipmentQueue.selectNone') : t('equipmentQueue.selectAll')}
                    </button>
                    {selected.size > 0 && (
                      <button className="btn btn-sm btn-primary" data-ph-capture="equipment_confirm_bulk"
                              onClick={confirmSelected}>
                        {t('equipmentQueue.confirmSelected', { count: selected.size })}
                      </button>
                    )}
                  </div>

                  {items.map((item) => (
                    <div key={item.id} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                      <input type="checkbox" checked={selected.has(item.id)}
                             onChange={() => toggle(item.id)}
                             style={{ marginTop: 16, flexShrink: 0 }}
                             aria-label={t('equipmentQueue.selectForConfirm')} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <QueueRow item={item} t={t} busy={busyId === item.id}
                                  onOpen={() => setCardId(item.id)}
                                  onConfirm={() => confirmOne(item)}
                                  onMerge={(d) => mergeInto(item, d)}
                                  onReject={() => reject(item)} />
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {cardId && (
        <EquipmentModal
          key={cardId}
          equipmentId={cardId}
          onClose={() => setCardId(null)}
          onSaved={() => { setCardId(null); after(); }}
          onOpenTask={openTask}
        />
      )}
    </>
  );
}
